---
meta:
  - title: Como usar Auri - IUSPRO Docs
  - description: Aprende a usar el asistente Auri.
---

# Como usar Auri

Auri es el asistente legal inteligente de IUSPRO. Puede preguntarle cualquier cosa de cualquier materia legal y él le responderá.

## ¿Qué puede hacer Auri?

Auri está enfocado en responder consultas legales porque tiene:

- **Toda la jurisprudencia de Costa Rica**
- **Toda la normativa costarricense**
- Su propio proceso de búsqueda que incluye:
  - Jurisprudencia relevante
  - Cambios de criterio
  - Criterios unificadores
  - Vinculación con la normativa

## Modos de respuesta

Auri permite elegir el enfoque con el que analizará una consulta:

- **Auto:** Analiza la consulta y decide si debe priorizar normativa, jurisprudencia o aspectos procesales. No impone una
  estructura fija: adapta la búsqueda y la respuesta al tipo de pregunta detectado.

- **Profundo:** En una consulta jurídica compleja inicial, crea un plan de investigación, divide el problema en asuntos y
  ejecuta en paralelo los especialistas que considere necesarios. Después valida las citas, contrasta los resultados,
  intenta cubrir vacíos de información y prepara una síntesis final. Consume **4 créditos**.

- **Procesal:** Se concentra en cómo hacer valer el derecho: vía procesal, tribunal competente, etapas, plazos, requisitos
  de admisibilidad, recursos, riesgos y posibles defectos procesales. La búsqueda también se orienta específicamente a
  fuentes procesales.

- **Fondo:** Se concentra en la existencia y alcance del derecho: normativa sustantiva, relación entre los hechos y los
  elementos jurídicos, interpretación jurisprudencial, derechos y obligaciones de las partes, argumentos de ambas
  posiciones y consecuencias jurídicas. Parte del supuesto de que el procedimiento se tramitará correctamente.

- **Integral:** Combina el análisis de fondo y el procesal. Primero determina la solidez de la posición jurídica; luego
  analiza cómo puede hacerse valer, sus plazos y obstáculos; finalmente integra ambos resultados en una evaluación de
  riesgos, oportunidades y recomendaciones estratégicas.

### Diferencia entre Integral y Profundo

**Integral define el alcance del análisis:** siempre cubre el fondo del asunto, el procedimiento y la estrategia.
**Profundo define el método de investigación:** planifica el trabajo, investiga el caso por asuntos, utiliza
especialistas en paralelo y aplica controles adicionales sobre las fuentes y conclusiones.

Profundo no ejecuta necesariamente cinco especialistas en todas las consultas. El sistema selecciona los que resulten
pertinentes para el caso. Además, su flujo especial está pensado para una consulta inicial apta para investigación
jurídica; las solicitudes de redacción, la búsqueda directa de una ley o resolución y las preguntas de seguimiento
utilizan el flujo más adecuado para esas tareas.

## Subir archivos

Puede subir archivos si quiere que Auri los resuma o analice. Simplemente súbalos y pídale lo que necesita. Por ejemplo, puede decirle "resúmelo" y él procesará el archivo.

## Historial de conversaciones

- Cada usuario tiene su propia historia de conversaciones
- Puede retomar cualquier hilo de conversación en cualquier momento
- Funciona como un chat donde puede mantener una conversación continua

## Descargar y compartir

Cada consulta que realice puede:

- **Descargarse en PDF** para tenerla guardada
- **Copiarse** para usarla en otros documentos
- **Compartirse** con otras personas

## Casos de uso comunes

Muchos usuarios utilizan Auri para:

- **Corroborar sus propios criterios legales**
- **Buscar criterios opuestos** a lo que ellos tienen
- **Iniciar un debate con la IA** para obtener un mejor entendimiento del tema

Este enfoque les ayuda a establecer más fuertemente su criterio como abogados al considerar diferentes perspectivas.

## Consejo: Redactar documentos

Si le pide a Auri que redacte un documento legal, él le proporcionará la opción de **redactarlo en el Redactor Inteligente**.

El Redactor está específicamente diseñado para la creación de documentos legales y ofrece:

- Búsqueda automática en códigos procesales
- Formato correcto según la ley vigente
- Mini chat para refinar el documento
- Exportación a PDF y Word
- Mucho más control sobre el documento final

Cuando Auri detecte que quiere redactar un documento, simplemente siga la opción que le presenta para usar el Redactor.

## Límites

No hay límite real en lo que puede hacer con Auri. El límite es su creatividad. Puede hacer absolutamente de todo en el ámbito legal costarricense.
